import * as Sentry from '@sentry/nextjs';
import { type ReactNode, useEffect, useState } from 'react';
import { Banner } from '@/components/ui/banner';
import { getUniversalCookie } from '@/cookies';
import { useSettings } from '@/hooks/state/useSettings';
import { logEvent } from '@/pages/api/v1/utils';
import { Button, toast } from '@music-vine/cadence/ui';

/**
 * Welcome-email campaigns that carry a discount code. The middleware persists
 * `utm_campaign` to a 30-day cookie, so landing from one of these emails shows
 * the matching code's banner. Campaigns not listed here never render a banner.
 */
const CAMPAIGN_PROMO_CODES: Record<string, string> = {
  'free-welcome_25discount_V1aspirational': 'WELCOME25',
  freewelcomestarterpacknb: 'SAVEYEARLY25'
};
const PROMO_BANNERS: Record<string, {
  description: ReactNode;
  title: string;
}> = {
  WELCOME25: {
    title: 'Claim your 25% annual discount',
    description: <>
          Use code <strong>WELCOME25</strong> for 25% off the advertised price
          of any annual subscription.
        </>
  },
  SAVEYEARLY25: {
    title: 'Claim your 25% annual discount',
    description: <>
          Use code <strong>SAVEYEARLY25</strong> for 25% off the advertised
          price of any annual subscription.
        </>
  }
};

/**
 * The discount code this visitor qualifies for, if any.
 *
 * Exported so `AppContainer` can tell whether this banner is going to claim
 * the top-sticky slot before it renders a lower-priority one beneath it.
 */
export const usePromoCouponCode = () => {
  // Read the cookie after mount — reading it during render would mismatch the
  // server-rendered HTML, which has no request cookies available.
  const [promoCode, setPromoCode] = useState<string>();
  const {
    settings
  } = useSettings();
  useEffect(() => {
    const campaign = getUniversalCookie<string>('utm_campaign');
    setPromoCode(campaign ? CAMPAIGN_PROMO_CODES[campaign] : undefined);
  }, []);
  if (settings?.isPremium) {
    return;
  }
  return promoCode && PROMO_BANNERS[promoCode] ? promoCode : undefined;
};
export const PromoCouponBanner = ({
  currentPath,
  condition = true
}: {
  currentPath: string;
  /** Extra gate so only one top-sticky banner can show at a time. */
  condition?: boolean;
}) => {
  const promoCode = usePromoCouponCode();
  const copy = promoCode ? PROMO_BANNERS[promoCode] : undefined;
  if (!(promoCode && copy)) {
    return null;
  }
  const bannerId = `promo-coupon-banner-${promoCode}`;
  const logBannerClick = (type: string) => {
    void logEvent({
      event_type: 'click',
      asPath: currentPath,
      metadata: {
        type,
        popup_id: bannerId,
        next_path: currentPath
      }
    });
  };
  const handleCopyCode = async () => {
    logBannerClick('cta');
    try {
      await navigator.clipboard.writeText(promoCode);
      toast.success('Code copied');
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          component: 'PromoCouponBanner',
          action: 'copy_code'
        }
      });
      toast.error("Couldn't copy code");
    }
  };
  return <Banner condition={condition} currentPath={currentPath} id={bannerId} onDismiss={() => logBannerClick('dismiss')} type="uppbeat" variant="top-sticky" data-sentry-element="Banner" data-sentry-component="PromoCouponBanner" data-sentry-source-file="promo-coupon.tsx">
      <div className="flex w-full grow flex-wrap items-center justify-center gap-2 pl-6 sm:gap-4 sm:pl-0">
        <span className="fl-text-sm/base">
          <strong>{copy.title}.</strong>{' '}
          <span className="hidden sm:inline">{copy.description}</span>
        </span>
        <Button onClick={handleCopyCode} size="sm" variant="contrast" data-sentry-element="Button" data-sentry-source-file="promo-coupon.tsx">
          Copy code
        </Button>
      </div>
    </Banner>;
};